import React, { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { XMarkIcon, ExclamationTriangleIcon, CheckCircleIcon, EnvelopeIcon } from '@heroicons/react/24/outline';
import apiClient from '../../../api/axios';

interface CreateUserModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

interface CreateUserData {
  email: string;
  firstName: string;
  lastName: string;
  isAttorney: boolean;
  roles: string[];
  temporaryPassword?: string;
}

function extractErrorMessage(error: unknown): string {
  const err = error as any;
  // AppError responses include `description` in the response body
  return err?.response?.data?.description
    || err?.response?.data?.message
    || err?.message
    || 'An unexpected error occurred. Please try again.';
}

export const CreateUserModal: React.FC<CreateUserModalProps> = ({
  isOpen,
  onClose,
  onSuccess
}) => {
  const [formData, setFormData] = useState<CreateUserData>({
    email: '',
    firstName: '',
    lastName: '',
    isAttorney: false,
    roles: []
  });

  const [selectedRoles, setSelectedRoles] = useState<string[]>([]);
  const [sendInvitation, setSendInvitation] = useState(true);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [successInfo, setSuccessInfo] = useState<{ email: string; invited: boolean } | null>(null);
  const queryClient = useQueryClient();

  const createUserMutation = useMutation({
    mutationFn: async (userData: CreateUserData) => {
      const response = await apiClient.post('/admin/users', {
        ...userData,
        roles: selectedRoles,
        sendInvitation
      });
      return response.data;
    },
    onSuccess: (_data, variables) => {
      setErrorMessage(null);
      queryClient.invalidateQueries({ queryKey: ['users'] });
      setSuccessInfo({ email: variables.email, invited: sendInvitation });
    },
    onError: (error: unknown) => {
      setErrorMessage(extractErrorMessage(error));
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMessage(null);
    createUserMutation.mutate(formData);
  };

  const handleRoleToggle = (role: string) => {
    setSelectedRoles(prev =>
      prev.includes(role)
        ? prev.filter(r => r !== role)
        : [...prev, role]
    );
  };

  const handleClose = () => {
    if (successInfo) {
      onSuccess();
    }
    setSuccessInfo(null);
    setErrorMessage(null);
    setFormData({ email: '', firstName: '', lastName: '', isAttorney: false, roles: [] });
    setSelectedRoles([]);
    setSendInvitation(true);
    onClose();
  };

  if (!isOpen) return null;

  // Success confirmation view
  if (successInfo) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-md mx-4">
          <div className="text-center py-4">
            <CheckCircleIcon className="h-12 w-12 text-green-500 mx-auto mb-3" />
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
              User Created Successfully
            </h2>
            {successInfo.invited ? (
              <div className="space-y-2">
                <div className="flex items-center justify-center gap-2 text-sm text-gray-600 dark:text-gray-300">
                  <EnvelopeIcon className="h-4 w-4" />
                  <span>Invitation email sent to</span>
                </div>
                <p className="text-sm font-medium text-gray-900 dark:text-white">
                  {successInfo.email}
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                  The user will receive a link to set their password. The link expires in 72 hours.
                </p>
              </div>
            ) : (
              <p className="text-sm text-gray-600 dark:text-gray-300">
                No invitation email was sent. You can set a password manually or resend the invitation later.
              </p>
            )}
          </div>
          <div className="flex justify-center pt-4">
            <Button onClick={handleClose}>
              Done
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-md mx-4">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
            Create New User
          </h2>
          <Button
            variant="ghost"
            size="sm"
            onClick={handleClose}
          >
            <XMarkIcon className="h-5 w-5" />
          </Button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Email *
            </label>
            <Input
              type="email"
              value={formData.email}
              onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
              required
              placeholder="user@example.com"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                First Name *
              </label>
              <Input
                value={formData.firstName}
                onChange={(e) => setFormData(prev => ({ ...prev, firstName: e.target.value }))}
                required
                placeholder="John"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Last Name *
              </label>
              <Input
                value={formData.lastName}
                onChange={(e) => setFormData(prev => ({ ...prev, lastName: e.target.value }))}
                required
                placeholder="Doe"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Roles
            </label>
            <div className="space-y-2">
              {['admin', 'attorney', 'billing', 'assistant', 'user'].map(role => (
                <label key={role} className="flex items-center">
                  <input
                    type="checkbox"
                    checked={selectedRoles.includes(role)}
                    onChange={() => handleRoleToggle(role)}
                    className="rounded border-gray-300 text-lex-accent focus:ring-lex-accent"
                  />
                  <span className="ml-2 text-sm text-gray-700 dark:text-gray-300 capitalize">
                    {role}
                  </span>
                </label>
              ))}
            </div>
          </div>

          <div className="flex items-center">
            <input
              type="checkbox"
              checked={formData.isAttorney}
              onChange={(e) => setFormData(prev => ({ ...prev, isAttorney: e.target.checked }))}
              className="rounded border-gray-300 text-lex-accent focus:ring-lex-accent"
            />
            <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
              Is Attorney
            </span>
          </div>

          <div className="flex items-center">
            <input
              type="checkbox"
              checked={sendInvitation}
              onChange={(e) => setSendInvitation(e.target.checked)}
              className="rounded border-gray-300 text-lex-accent focus:ring-lex-accent"
            />
            <div className="ml-2">
              <span className="text-sm text-gray-700 dark:text-gray-300">
                Send invitation email
              </span>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                User will receive an email with a link to set their password
              </p>
            </div>
          </div>

          {/* Error display — EdgeLex styled with icon and clear messaging */}
          {errorMessage && (
            <div className="flex items-start gap-3 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
              <ExclamationTriangleIcon className="h-5 w-5 text-red-500 dark:text-red-400 shrink-0 mt-0.5" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-red-800 dark:text-red-300">
                  User Creation Failed
                </p>
                <p className="text-sm text-red-600 dark:text-red-400 mt-0.5">
                  {errorMessage}
                </p>
              </div>
              <button
                type="button"
                onClick={() => setErrorMessage(null)}
                className="shrink-0 text-red-400 hover:text-red-600 dark:hover:text-red-300"
              >
                <XMarkIcon className="h-4 w-4" />
              </button>
            </div>
          )}

          <div className="flex justify-end space-x-3 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={createUserMutation.isPending}
            >
              {createUserMutation.isPending ? 'Creating...' : 'Create User'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};
